/// <reference path="../../../../../../../../node_modules/@flowx/monaco-editor/monaco.d.ts" />
import { APP_BASE_HREF } from '@angular/common'
import { Injectable, OnDestroy, computed, effect, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Store } from '@ngrx/store'

import {
  EditorWithFunctionReferences,
  editorForModel,
} from '@flowx/designer/modules/monaco-code-editor/types'
import {
  REUSABLE_FUNCTION_REFERENCE_PATTERN,
  ReusableFunctionSummaryWithManifestInfo,
} from '@flowx/designer/modules/reusable-resources/reusable-functions/models/reusable-function.entity'
import { reusableFunctionsWithAppNameMap } from '@flowx/designer/shared/store/selectors/reusable-functions.selectors'
import {
  selectAppId,
  selectAppVersionId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import {
  FUNCTION_REFERENCE_DECORATION_CLASS,
  FunctionRefTarget,
  LANGUAGE_LABELS,
  NAVIGABLE_SCRIPT_LANGUAGES,
  PLAINTEXT_LANGUAGE_ID,
  buildTargetIndex,
  functionDefinitionCommands,
  functionPreviewContent,
  functionPreviewMarkdown,
  isNavigableScriptLanguage,
  parseTargetUri,
  targetUriFor,
} from './function-navigation.model'

/** A `functions.<app>.<name>` occurrence in a model, and where it sits. */
type FunctionReferenceMatch = { fullName: string; range: monaco.IRange }

/**
 * Direct navigation between functions.
 *
 * Registers ONE global set of Monaco providers, so every script editor in the
 * designer gets navigable `functions.<app>.<name>(...)` references at once — the
 * function editor, the business-rule editor, the test modals, the workflow
 * script and fork nodes — rather than each call site wiring its own. Registered
 * once from AppComponent after Monaco loads; the React editors share the same
 * `window.monaco`, so they are covered by the same registration.
 *
 * A resolved reference is underlined at rest so it reads as navigable, names
 * the function it points at on hover, and is opened with Monaco's own
 * go-to-definition — cmd/ctrl+click, F12, the editor context menu and peek all
 * lead to the same place.
 *
 * Resolution is language-scoped: a reference resolves only to a reusable
 * function written in the same language as the script referencing it, because
 * that is the only thing the runtime honours — the `functions` namespace is
 * built per language by `GraalFunctionsInjector` / `NativeFunctionsMap`. A
 * cross-language name gets a non-navigating hover saying so, the one case where
 * the platform knows exactly what is wrong; everything else stays plain text.
 *
 * Clicking always opens the definition in a new browser tab. A called function
 * is not edited inside its parent, and the script you came from stays open and
 * untouched — no lost place, no unsaved-changes prompt.
 *
 * Built on go-to-definition rather than Monaco's link provider. The link
 * provider is a smaller seam for "this range opens a URL", and it draws its own
 * underline; the definition route costs an underline decoration and the
 * `gotoLocation` and `#lastResolved` workarounds below, and buys F12, the editor
 * context menu and peek on the same reference. That is the deliberate trade.
 */
@Injectable({ providedIn: 'root' })
export class FunctionNavigationService implements OnDestroy {
  #ngrxStore = inject(Store)
  #router = inject(Router)
  #baseHref = inject(APP_BASE_HREF)

  #storeFunctions = this.#ngrxStore.selectSignal(reusableFunctionsWithAppNameMap)
  #currentAppId = this.#ngrxStore.selectSignal(selectAppId)
  #currentAppVersionId = this.#ngrxStore.selectSignal(selectAppVersionId)
  #currentWorkspaceId = this.#ngrxStore.selectSignal(selectWorkspaceId)

  #disposables: monaco.IDisposable[] = []
  #definitionProvider: monaco.IDisposable | null = null
  #previewModels: monaco.editor.ITextModel[] = []
  #registered = false

  /** The underline drawn on every resolved reference, per editor. */
  #underlines = new Map<monaco.editor.ICodeEditor, monaco.editor.IEditorDecorationsCollection>()
  #editorDisposables = new Map<monaco.editor.ICodeEditor, monaco.IDisposable[]>()

  /** Per editor, the subscription to its *current* model's language. */
  #languageListeners = new Map<monaco.editor.ICodeEditor, monaco.IDisposable>()

  /**
   * Every reference in a model, cached against the version it was read from.
   *
   * One scan answers the underline, the hover and the definition for as long as
   * the text is unchanged, so typing costs one pass over the document rather
   * than one per event — and, more importantly, all three read the same list, so
   * what is underlined is exactly what is clickable.
   */
  #matchCache = new WeakMap<
    monaco.editor.ITextModel,
    { versionId: number; matches: FunctionReferenceMatch[] }
  >()

  /**
   * The reference the definition provider answered last, and where it was.
   * Read back by `#openCodeEditor` when Monaco opens someone else's answer to
   * the same question — see the comment there.
   */
  #lastResolved: { modelUri: string; range: monaco.IRange; target: FunctionRefTarget } | null = null

  /**
   * Whether the go-to-definition modifier is down. Tracked on the document
   * rather than per editor, so it is right even when no editor holds keyboard
   * focus, and right for a modifier pressed while the pointer is already still.
   */
  #triggerModifierHeld = false

  /**
   * Monaco caches a model's answers until the model changes, so a provider
   * registered before the reusable functions have loaded would keep answering
   * "no definition" on an untouched script — the maker would have to type
   * something before any reference became clickable. Re-registering invalidates
   * that cache, so references become navigable as soon as the list arrives.
   */
  #refreshProviderOnTargetsFx = effect(() => {
    this.#targetsByLanguage()

    if (this.#registered) {
      this.#registerDefinitionProvider()
      this.#underlines.forEach((_, editor) => this.#underlineReferences(editor))
    }
  })

  #targetsByLanguage = computed(() =>
    buildTargetIndex(
      (this.#storeFunctions() ?? {}) as Record<string, ReusableFunctionSummaryWithManifestInfo>
    )
  )

  /** Registers the global definition provider, hover provider and opener. */
  register(): void {
    if (this.#registered) {
      return
    }
    this.#registered = true

    this.#disposables = [
      monaco.languages.registerHoverProvider([...NAVIGABLE_SCRIPT_LANGUAGES], {
        provideHover: (model, position) => this.#referenceHover(model, position),
      }),
      // Monaco asks for a resource other than the one in the editor to be
      // opened, and we open it as a tab instead of swapping the editor's model.
      monaco.editor.registerEditorOpener({
        openCodeEditor: (source, resource) => this.#openCodeEditor(source, resource),
      }),
      monaco.editor.onDidCreateEditor((editor) => this.#trackEditor(editor)),
      ...this.#trackTriggerModifier(),
    ]

    monaco.editor.getEditors().forEach((editor) => this.#trackEditor(editor))
    this.#registerDefinitionProvider()
  }

  ngOnDestroy(): void {
    this.#disposables.forEach((disposable) => disposable.dispose())
    this.#disposables = []
    this.#definitionProvider?.dispose()
    this.#definitionProvider = null
    this.#previewModels.forEach((model) => !model.isDisposed() && model.dispose())
    this.#previewModels = []
    this.#underlines.forEach((collection) => collection.clear())
    this.#underlines.clear()
    this.#editorDisposables.forEach((disposables) =>
      disposables.forEach((disposable) => disposable.dispose())
    )
    this.#editorDisposables.clear()
    this.#languageListeners.forEach((listener) => listener.dispose())
    this.#languageListeners.clear()
    this.#registered = false
  }

  /**
   * Keeps `#triggerModifierHeld` up to date from the document, in capture phase
   * so Monaco cannot swallow the event first.
   *
   * Three listeners for the whole designer rather than four on every Monaco
   * editor it ever creates — the flag is one global boolean, and most editors in
   * the designer (SQL, Mongo, JSON) can never hold a reference at all.
   */
  #trackTriggerModifier(): monaco.IDisposable[] {
    const read = (event: KeyboardEvent | MouseEvent): void => {
      this.#triggerModifierHeld = event.ctrlKey || event.metaKey
    }

    return (['keydown', 'keyup', 'mousemove'] as const).map((type) => {
      document.addEventListener(type, read, { capture: true })
      return { dispose: () => document.removeEventListener(type, read, { capture: true }) }
    })
  }

  /**
   * Opens a reference in its own tab from a place with no anchor to click — a
   * cmd-click in the editor, which arrives as a bare URI.
   *
   * The href is serialized by the router from a base href plus the definition's
   * commands, the way every other open-in-a-new-tab in the designer builds one
   * (see `ResourceUsagesReferencesComponent.navigateToResource`). A hand-joined
   * path would drop the base href, which is what a deployment under a sub-path
   * — a preview environment — is served from.
   *
   * A synthesised link click rather than `window.open`: a popup blocker can
   * refuse `window.open` and reports nothing back when it does, whereas a link
   * click is the navigation browsers always honour.
   */
  #openInNewTab(target: FunctionRefTarget): void {
    const commands = functionDefinitionCommands(target, {
      workspaceId: this.#currentWorkspaceId(),
      appId: this.#currentAppId(),
      appVersionId: this.#currentAppVersionId(),
    })

    const link = document.createElement('a')
    link.href = this.#router.serializeUrl(this.#router.createUrlTree([this.#baseHref, ...commands]))
    link.target = '_blank'
    link.rel = 'noopener'
    link.click()
  }

  /**
   * Starts underlining references in an editor and keeps it up to date.
   *
   * The initial pass is deferred to a microtask because both wrappers stamp
   * `flxCanReferenceFunctions` on the instance in the same synchronous block
   * that creates it, immediately after `monaco.editor.create` — which is what
   * fires this. Decorating straight away would read the flag before it is set
   * and leave the surface bare until the first keystroke.
   */
  #trackEditor(editor: monaco.editor.ICodeEditor): void {
    if (this.#underlines.has(editor)) {
      return
    }

    const collection = editor.createDecorationsCollection()
    this.#underlines.set(editor, collection)

    this.#editorDisposables.set(editor, [
      editor.onDidChangeModel(() => {
        this.#trackModelLanguage(editor)
        this.#underlineReferences(editor)
      }),
      editor.onDidChangeModelContent(() => this.#underlineReferences(editor)),
      editor.onDidDispose(() => this.#forgetEditor(editor)),
    ])

    this.#trackModelLanguage(editor)
    queueMicrotask(() => this.#underlineReferences(editor))
  }

  /**
   * Re-underlines when the model's language changes under a live editor.
   *
   * Its own subscription because a language change is neither a model change
   * nor a content change, and Monaco raises it on the model rather than on the
   * editor. The React wrapper switches a script node between JavaScript and
   * Python with `monaco.editor.setModelLanguage` on the model already open, so
   * without this the underlines drawn for the old language stay on a script
   * that no longer resolves them — underlined but not clickable, which is the
   * one thing this service promises cannot happen. The Angular wrapper rebuilds
   * its editor on an options change and so is covered either way.
   *
   * Kept separate from the editor's own disposables because it has to be torn
   * down and re-made every time the editor is given a different model.
   */
  #trackModelLanguage(editor: monaco.editor.ICodeEditor): void {
    this.#languageListeners.get(editor)?.dispose()
    this.#languageListeners.delete(editor)

    const model = editor.getModel()
    if (!model) {
      return
    }

    this.#languageListeners.set(
      editor,
      model.onDidChangeLanguage(() => this.#underlineReferences(editor))
    )
  }

  #forgetEditor(editor: monaco.editor.ICodeEditor): void {
    this.#editorDisposables.get(editor)?.forEach((disposable) => disposable.dispose())
    this.#editorDisposables.delete(editor)
    this.#languageListeners.get(editor)?.dispose()
    this.#languageListeners.delete(editor)
    this.#underlines.delete(editor)
  }

  /**
   * Draws the underline on every reference the definition provider would
   * navigate — the same resolution, so what is underlined and what is clickable
   * can never disagree. Go-to-definition only underlines while cmd/ctrl is held,
   * so this is what makes a reference read as navigable at rest.
   */
  #underlineReferences(editor: monaco.editor.ICodeEditor): void {
    const collection = this.#underlines.get(editor)
    if (!collection) {
      return
    }

    const model = editor.getModel()

    collection.set(
      (model ? this.#resolvableReferences(model, editor) : []).map(({ range }) => ({
        range,
        options: {
          description: FUNCTION_REFERENCE_DECORATION_CLASS,
          inlineClassName: FUNCTION_REFERENCE_DECORATION_CLASS,
        },
      }))
    )
  }

  #registerDefinitionProvider(): void {
    this.#definitionProvider?.dispose()
    this.#definitionProvider = monaco.languages.registerDefinitionProvider(
      [...NAVIGABLE_SCRIPT_LANGUAGES],
      { provideDefinition: (model, position) => this.#definitionAt(model, position) }
    )
  }

  /**
   * Whether the editor holding `model` is a surface whose scripts can call
   * reusable functions at all — see `EditorWithFunctionReferences`, which states
   * the rule and why the declaration travels on the editor instance.
   *
   * `editor` is passed wherever the caller already has it, so the common path
   * (re-underlining as the maker types) never pays for the lookup.
   */
  #modelCanReferenceFunctions(
    model: monaco.editor.ITextModel,
    editor?: monaco.editor.ICodeEditor
  ): boolean {
    const host = (editor ?? editorForModel(model)) as EditorWithFunctionReferences | undefined

    return !!host?.flxCanReferenceFunctions
  }

  #targetsFor(language: string | null): Map<string, FunctionRefTarget> | null {
    return isNavigableScriptLanguage(language) ? this.#targetsByLanguage().get(language) : null
  }

  /**
   * Every reference in `model`, whether or not it names a function that exists.
   *
   * Read off the whole document, never a single line: the pattern's `(?=\s*\()`
   * lookahead accepts a newline between the name and its opening paren, so a
   * call broken over two lines is a real reference that a per-line scan would
   * miss. Everything downstream — the underline, the hover, the definition —
   * works from this one list, so none of them can see a reference the others
   * cannot.
   */
  #referencesIn(model: monaco.editor.ITextModel): FunctionReferenceMatch[] {
    const versionId = model.getVersionId()
    const cached = this.#matchCache.get(model)

    if (cached?.versionId === versionId) {
      return cached.matches
    }

    const matches = [...model.getValue().matchAll(REUSABLE_FUNCTION_REFERENCE_PATTERN)].map(
      (match) => {
        const start = model.getPositionAt(match.index)
        const end = model.getPositionAt(match.index + match[0].length)

        return {
          fullName: match[1],
          range: {
            startLineNumber: start.lineNumber,
            startColumn: start.column,
            endLineNumber: end.lineNumber,
            endColumn: end.column,
          },
        }
      }
    )

    this.#matchCache.set(model, { versionId, matches })

    return matches
  }

  /** The references in `model` that resolve — the ones worth underlining. */
  #resolvableReferences(
    model: monaco.editor.ITextModel,
    editor?: monaco.editor.ICodeEditor
  ): FunctionReferenceMatch[] {
    const targets = this.#targetsFor(model.getLanguageId())
    if (!targets || !this.#modelCanReferenceFunctions(model, editor)) {
      return []
    }

    return this.#referencesIn(model).filter((reference) => targets.has(reference.fullName))
  }

  /**
   * The reference under `position`, if the position is on one. Both the
   * definition provider and the cross-language hover work from a position
   * rather than the whole model, so they share this.
   */
  #referenceAt(
    model: monaco.editor.ITextModel,
    position: monaco.Position
  ): FunctionReferenceMatch | null {
    return (
      this.#referencesIn(model).find((reference) =>
        monaco.Range.containsPosition(reference.range, position)
      ) ?? null
    )
  }

  /**
   * The go-to-definition answer for a reference: cmd/ctrl+click, F12, the editor
   * context menu and peek all come from this one provider.
   *
   * The definition is addressed to a model of our own rather than to nothing,
   * because Monaco resolves the target model before it will draw anything: the
   * cmd-hover underline is added only once the model behind the definition has
   * been read, so a definition pointing at a resource with no model is a link
   * the maker can never see. That model holds the one line the preview shows —
   * the summary a reference resolves against does not carry the script.
   */
  #definitionAt(
    model: monaco.editor.ITextModel,
    position: monaco.Position
  ): monaco.languages.LocationLink[] | null {
    const targets = this.#targetsFor(model.getLanguageId())
    if (!targets || !this.#modelCanReferenceFunctions(model)) {
      return null
    }

    const reference = this.#referenceAt(model, position)
    const target = reference && targets.get(reference.fullName)
    if (!target) {
      // Nothing here to fall back to, and Monaco asks on every hover as well as
      // every click, so this is also what keeps the fallback from acting on an
      // answer that has since been asked again elsewhere.
      this.#lastResolved = null
      return null
    }

    const preview = this.#previewModel(target)
    this.#lastResolved = { modelUri: model.uri.toString(), range: reference.range, target }

    return [
      {
        uri: preview.uri,
        // What Monaco renders in the cmd-hover preview and in the peek widget.
        range: preview.getFullModelRange(),
        // The words the underline is drawn under.
        originSelectionRange: reference.range,
        // Deliberately a range in the SOURCE model, not in `preview`.
        //
        // `registerEditorOpener` hands our opener back as the editor that
        // handled the navigation, so the editor Monaco then flashes
        // (`goToCommands`, 350ms of `symbolHighlight`) is the one the click came
        // from — and it draws that flash on this range. Pointing it at the
        // reference makes the flash confirm what was clicked; `preview`'s own
        // range would flash line 1 of the script instead.
        //
        // The peek widget is the one place this range is read against `preview`,
        // and Monaco validates it there, so a reference below line 1 is clamped
        // to the single preview line rather than breaking the selection.
        targetSelectionRange: reference.range,
      },
    ]
  }

  /**
   * The model behind a definition. Keyed by the target's URI, so a function
   * referenced from twenty scripts has one preview, and rebuilt in place when
   * the function it describes changes.
   *
   * Plain text on purpose: a model whose language is JavaScript would be
   * synchronised into Monaco's TypeScript worker and its contents would start
   * turning up in the autocomplete and diagnostics of the real editors.
   */
  #previewModel(target: FunctionRefTarget): monaco.editor.ITextModel {
    const uri = targetUriFor(target.language, target.fullName)
    const content = functionPreviewContent(target)
    const existing = monaco.editor.getModel(uri)

    if (existing) {
      if (existing.getValue() !== content) {
        existing.setValue(content)
      }
      return existing
    }

    const model = monaco.editor.createModel(content, PLAINTEXT_LANGUAGE_ID, uri)
    this.#previewModels.push(model)

    return model
  }

  /**
   * What a plain hover over a reference says.
   *
   * A resolved reference gets the same line Monaco shows in its own preview
   * while the modifier is held — which is why it is answered only when the
   * modifier is up: Monaco composes that preview into the same hover, so
   * answering here as well would print the line twice.
   *
   * An unresolved name stays silent, with one exception: it matches a real
   * function in a real application, but that function is written in the other
   * language, so the runtime will never bind it. Left silent it looks exactly
   * like a typo, which is the one thing it is not.
   */
  #referenceHover(
    model: monaco.editor.ITextModel,
    position: monaco.Position
  ): monaco.languages.Hover | null {
    const scriptLanguage = model.getLanguageId()
    if (!isNavigableScriptLanguage(scriptLanguage) || !this.#modelCanReferenceFunctions(model)) {
      return null
    }

    const reference = this.#referenceAt(model, position)
    if (!reference) {
      return null
    }

    const target = this.#targetsFor(scriptLanguage)?.get(reference.fullName)
    if (target) {
      return this.#triggerModifierHeld
        ? null
        : { range: reference.range, contents: [{ value: functionPreviewMarkdown(target) }] }
    }

    const otherLanguage = NAVIGABLE_SCRIPT_LANGUAGES.find(
      (language) =>
        language !== scriptLanguage && !!this.#targetsFor(language)?.has(reference.fullName)
    )

    return otherLanguage
      ? {
          range: reference.range,
          contents: [
            {
              value: `\`${reference.fullName}\` is a ${LANGUAGE_LABELS[otherLanguage]} function; this script is ${LANGUAGE_LABELS[scriptLanguage]}. A reference only resolves within the same language.`,
            },
          ],
        }
      : null
  }

  #openResource(resource: monaco.Uri): boolean {
    const parsed = parseTargetUri(resource)
    const target = parsed && this.#targetsFor(parsed.language)?.get(parsed.fullName)

    if (!target) {
      return false
    }

    this.#openInNewTab(target)
    return true
  }

  #openCodeEditor(source: monaco.editor.ICodeEditor, resource: monaco.Uri): boolean {
    if (this.#openResource(resource)) {
      return true
    }

    // The one case where Monaco can hand us someone else's answer to our
    // question. On a JavaScript script the injected `functions` typings declare
    // every reusable function, so Monaco's own TypeScript provider answers the
    // same go-to-definition with a location in `ts:definitions.d.ts` — a file
    // with no model behind it, so acting on it would do nothing at all. Which of
    // the two answers Monaco picks depends on the order the providers happened
    // to register in; either way the maker asked to open the reference under the
    // cursor, and that is the one we just resolved.
    const position = source?.getPosition()
    const resolved = this.#lastResolved

    if (
      !position ||
      !resolved ||
      source.getModel()?.uri.toString() !== resolved.modelUri ||
      !monaco.Range.containsPosition(resolved.range, position)
    ) {
      return false
    }

    this.#openInNewTab(resolved.target)
    return true
  }
}
